import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ChatModal from '../components/ChatModal';
import { User, Store } from '../types';
import { Users as UIcon, User as UserIcon, Shield, Check, MessageCircle, Search, Plus, Pencil, Trash2, X, RefreshCw } from 'lucide-react';
import { subscribeToChatEvents } from '../utils/chatEvents';
import { getApiBase } from '../utils/apiBase';

const primary = '#F1561D';

const s = {
  page: { padding: '32px', background: '#f8f7f5', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" } as React.CSSProperties,
  inp: { width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '9px 12px', fontSize: '14px', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, background: '#fff' },
  label: { fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '4px', display: 'block' },
  overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0' },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' },
  modalBody: { padding: '20px 24px', display: 'flex', flexDirection: 'column' as const, gap: '12px' },
  modalFooter: { padding: '0 24px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '14px', fontWeight: 500, color: '#374151', cursor: 'pointer' },
  saveBtn: { padding: '9px 18px', borderRadius: '8px', border: 'none', background: primary, color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  pill: (color: string, bg: string) => ({ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, color, background: bg }),
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><span style={s.label}>{label}</span>{children}</div>
);

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'user', password: '', isActive: true });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'customers' | 'staff' | 'messaging'>('customers');
  const [chatCustomer, setChatCustomer] = useState<User | null>(null);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [flashTab, setFlashTab] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState('');

  const API_BASE = getApiBase();
  const token = localStorage.getItem('authToken');

  // Get current user role
  const storedUser = localStorage.getItem('authUser');
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const currentUserRole = currentUser?.role || '';
  const isAdmin = currentUserRole === 'admin';
  const isDev = currentUserRole === 'dev';

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (totalUnread > 0 && activeTab !== 'messaging') {
      let show = true;
      interval = setInterval(() => {
        setFlashTab(show);
        document.title = show ? `🔴 (${totalUnread}) User Management` : 'User Management';
        show = !show;
      }, 1000);
    } else { setFlashTab(false); document.title = 'User Management'; }
    return () => clearInterval(interval);
  }, [totalUnread, activeTab]);

  const fetchUnreadCounts = async () => {
    // Only fetch unread counts if user is admin
    if (!isAdmin) return;
    
    try {
      const res = await axios.get(`${API_BASE}/api/chat/conversations`, { headers: { Authorization: `Bearer ${token}` }, params: { status: 'open' } });
      const conversations = res.data.data || [];
      const counts: Record<string, number> = {};
      let total = 0;
      conversations.forEach((conv: any) => {
        const customerId = conv.customerId?._id || conv.customerId;
        const unreadCount = conv.unreadCount || 0;
        if (customerId) { counts[customerId] = unreadCount; total += unreadCount; }
      });
      setUnreadCounts(prev => {
        const keys = new Set([...Object.keys(prev), ...Object.keys(counts)]);
        const changed = [...keys].some(k => counts[k] !== prev[k]);
        return changed ? counts : prev;
      });
      setTotalUnread(prev => prev !== total ? total : prev);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!isAdmin || !token) return;
    const subscription = subscribeToChatEvents(API_BASE, token, event => {
      if (event.type === 'chat.message.created' || event.type === 'chat.unread.changed') {
        fetchUnreadCounts();
      }
    });
    return () => subscription.close();
  }, [API_BASE, token, isAdmin]);

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE}/api/users/register`, newUser, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => [res.data.data, ...prev]);
      setShowAddModal(false);
      setNewUser({ name: '', email: '', role: 'user', password: '', isActive: true });
    } catch (err) { console.error(err); }
  };

  const deleteUser = async (id: string) => {
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`${API_BASE}/api/users/${id}`, { headers: { Authorization: `Bearer ${token}` } }); setUsers(prev => prev.filter(u => u._id !== id)); }
    catch (err) { console.error(err); }
  };

  const updateUser = async (id: string, updates: Partial<User>) => {
    try {
      const res = await axios.put(`${API_BASE}/api/users/${id}`, updates, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.map(u => u._id === id ? res.data.data : u));
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [uRes, sRes] = await Promise.all([
          axios.get(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setUsers(uRes.data.data);
        setStores(sRes.data.data || sRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
      
      // Only fetch unread counts if user is admin
      if (isAdmin) {
        await fetchUnreadCounts();
      }
    };
    load();
  }, [API_BASE, token, isAdmin]);

  if (loading) return <div style={{ padding: '32px', fontFamily: 'DM Sans, sans-serif', color: '#9ca3af' }}>Loading users…</div>;

  const customers = users.filter(u => u.role === 'customer');
  const staff = users.filter(u => u.role === 'admin' || u.role === 'manager');
  const userList = activeTab === 'customers' ? customers : staff;
  const filtered = userList.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()));

  const initials = (name: string) => (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const avatarColors = ['#fee2e2','#fef3c7','#d1fae5','#dbeafe','#ede9fe'];
  const avatarText = ['#dc2626','#d97706','#059669','#2563eb','#7c3aed'];
  const avatarColor = (name: string) => { const i = (name || '').charCodeAt(0) % avatarColors.length; return { bg: avatarColors[i], text: avatarText[i] }; };

  return (
    <div style={s.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827' }}>Users</h1>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#9ca3af' }}>{users.length} total · {customers.length} customers · {staff.length} staff</p>
        </div>
        <button onClick={() => setShowAddModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', background: primary, color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
          <Plus size={16} /> Add User
        </button>
      </div>

      {/* ── Stat pills ──────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Total Users', value: users.length, color: '#111827', bg: '#fff', accent: '#6b7280' },
          { label: 'Customers', value: customers.length, color: '#2563eb', bg: '#eff6ff', accent: '#2563eb' },
          { label: 'Staff', value: staff.length, color: '#7c3aed', bg: '#f5f3ff', accent: '#7c3aed' },
          { label: 'Active', value: users.filter(u => u.isActive).length, color: '#059669', bg: '#f0fdf4', accent: '#059669' },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #f3f4f6', borderLeft: `4px solid ${c.accent}` }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.label}</p>
            <p style={{ margin: '6px 0 0', fontSize: '28px', fontWeight: 700, color: c.color, lineHeight: 1 }}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '20px', gap: '4px' }}>
        {([
          { key: 'customers' as const, label: `Customers`, icon: <UIcon size={15} />, count: customers.length },
          { key: 'staff' as const, label: 'Staff', icon: <Shield size={15} />, count: staff.length },
          ...(isAdmin ? [{ key: 'messaging' as const, label: 'Messaging', icon: <MessageCircle size={15} />, badge: totalUnread }] : []),
        ]).map(tab => {
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as typeof activeTab)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 18px', fontSize: '14px', fontWeight: 500, border: 'none', background: 'none', cursor: 'pointer', color: active ? primary : '#6b7280', borderBottom: active ? `2px solid ${primary}` : '2px solid transparent', marginBottom: '-1px', position: 'relative' }}>
              {tab.icon}
              {tab.label}
              {'count' in tab && <span style={{ fontSize: '11px', background: '#f3f4f6', color: '#6b7280', borderRadius: '20px', padding: '1px 7px' }}>{tab.count}</span>}
              {'badge' in tab && (tab.badge ?? 0) > 0 && activeTab !== 'messaging' && (
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', position: 'absolute', top: '8px', right: '8px', animation: flashTab ? 'pulse 1s infinite' : 'none' }} />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Search (not on messaging tab) ──────────────────────────────── */}
      {activeTab !== 'messaging' && (
        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input type="text" placeholder={`Search ${activeTab}…`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ ...s.inp, paddingLeft: '40px' }} />
        </div>
      )}

      {/* ── Messaging tab (admin only) ───────────────────────────────────── */}
      {isAdmin && activeTab === 'messaging' && (
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f3f4f6', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                {['Customer', 'Email', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.map(customer => {
                const unread = unreadCounts[customer._id] || 0;
                const av = avatarColor(customer.name);
                return (
                  <tr key={customer._id} style={{ borderBottom: '1px solid #f9fafb', background: unread > 0 ? '#fff9f7' : 'transparent' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: av.bg, color: av.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>{initials(customer.name)}</div>
                        <span style={{ fontWeight: 500, color: unread > 0 ? '#b91c1c' : '#111827', fontSize: '14px' }}>{customer.name}</span>
                        {unread > 0 && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />}
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: '13px', color: unread > 0 ? '#dc2626' : '#6b7280' }}>{customer.email}</td>
                    <td style={{ padding: '14px 20px' }}>
                      {unread > 0 ? (
                        <span style={{ background: '#fee2e2', color: '#b91c1c', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px' }}>{unread} new</span>
                      ) : (
                        <span style={{ background: '#f3f4f6', color: '#9ca3af', fontSize: '11px', padding: '4px 10px', borderRadius: '20px' }}>No messages</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <button onClick={() => setChatCustomer(customer)}
                        style={{ background: unread > 0 ? primary : '#f3f4f6', color: unread > 0 ? '#fff' : '#374151', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                        {unread > 0 ? 'View Messages' : 'Chat'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {customers.length === 0 && <p style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '14px' }}>No customers yet</p>}
        </div>
      )}

      {/* ── Users table ─────────────────────────────────────────────────── */}
      {activeTab !== 'messaging' && (
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f3f4f6', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                {['User', 'Email', ...(activeTab === 'staff' ? ['Role'] : []), 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(user => {
                const av = avatarColor(user.name);
                return (
                  <tr key={user._id} style={{ borderBottom: '1px solid #f9fafb' }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = '#fafafa'}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: av.bg, color: av.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>{initials(user.name)}</div>
                        <span style={{ fontWeight: 500, color: '#111827', fontSize: '14px' }}>{user.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: '13px', color: '#6b7280' }}>{user.email}</td>
                    {activeTab === 'staff' && (
                      <td style={{ padding: '14px 20px' }}>
                        <span style={s.pill(user.role === 'admin' ? '#6d28d9' : '#c2410c', user.role === 'admin' ? '#ede9fe' : '#fff5f2')}>{user.role}</span>
                      </td>
                    )}
                    <td style={{ padding: '14px 20px' }}>
                      <span style={s.pill(user.isActive ? '#065f46' : '#991b1b', user.isActive ? '#d1fae5' : '#fee2e2')}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => { setEditingUser(user); setEditForm({ name: user.name, email: user.email }); setSelectedStore(user.storeId || ''); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '12px', fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
                          <Pencil size={12} /> Edit
                        </button>
                        {activeTab === 'staff' && (
                          <button onClick={() => updateUser(user._id, { role: user.role === 'admin' ? 'manager' : 'admin' })}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e0d9ff', background: '#f5f3ff', fontSize: '12px', fontWeight: 500, color: '#6d28d9', cursor: 'pointer' }}>
                            <RefreshCw size={12} /> Toggle Role
                          </button>
                        )}
                        <button onClick={() => deleteUser(user._id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #fee2e2', background: '#fff5f5', fontSize: '12px', fontWeight: 500, color: '#dc2626', cursor: 'pointer' }}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <p style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '14px' }}>No {activeTab} found</p>}
        </div>
      )}

      {/* ── Add User Modal ───────────────────────────────────────────────── */}
      {showAddModal && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Add User</h3>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <form onSubmit={addUser}>
              <div style={s.modalBody}>
                <Field label="Name"><input style={s.inp} placeholder="Full name" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} required /></Field>
                <Field label="Email"><input style={s.inp} type="email" placeholder="user@email.com" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required /></Field>
                <Field label="Password"><input style={s.inp} type="password" placeholder="Password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required /></Field>
                <Field label="Role">
                  <select style={s.inp} value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                    <option value="user">Customer</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </Field>
              </div>
              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" style={s.saveBtn}>Add User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit User Modal ──────────────────────────────────────────────── */}
      {editingUser && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setEditingUser(null); }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Edit User</h3>
              <button onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <form onSubmit={async e => {
              e.preventDefault();
              const updates: any = { ...editForm };
              if (editingUser?.role?.toLowerCase() === 'manager' && selectedStore) updates.storeId = selectedStore;
              await updateUser(editingUser._id, updates);
              setEditingUser(null);
            }}>
              <div style={s.modalBody}>
                <Field label="Name"><input style={s.inp} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required /></Field>
                <Field label="Email"><input style={s.inp} type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} required /></Field>
                {(editingUser.role === 'manager' || editingUser.role === 'admin') && (
                  <Field label="Assigned Store">
                    <select style={s.inp} value={selectedStore} onChange={e => setSelectedStore(e.target.value)}>
                      <option value="">No store assigned</option>
                      {stores.map(st => <option key={st._id} value={st._id}>{st.name}</option>)}
                    </select>
                  </Field>
                )}
              </div>
              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => setEditingUser(null)}>Cancel</button>
                <button type="submit" style={s.saveBtn}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Chat modal (admin only) ──────────────────────────────────────── */}
      {isAdmin && chatCustomer && (
        <ChatModal customer={chatCustomer} token={token!} onClose={() => { setChatCustomer(null); fetchUnreadCounts(); }} />
      )}
    </div>
  );
};

export default Users;
